<template>
  <div class="page-col" :class="colClasses" :style="colStyle">
    <!-- Drag handle slot for edit mode -->
    <slot name="drag-handle" />
    <!-- Column content -->
    <slot />
  </div>
</template>

<script>
/**
 * ColNode - A fluid column component for the page builder
 *
 * Width props accept:
 * - Number 1-12: Traditional 12-column grid (e.g., 6 = 50%)
 * - String percentage: Direct percentage (e.g., '70%', '33.33%')
 * - String 'auto': Content-based width
 * - String 'grow': Fill remaining space (flex-grow: 1)
 *
 * Example usage:
 * - cols="12" md="6" lg="4"  → Full width on mobile, half on tablet, third on desktop
 * - cols="100%" md="70%"    → 100% mobile, 70% tablet+
 * - cols="grow"             → Fill remaining space
 */
export default {
  name: 'ColNode',
  props: {
    nodeId: String,
    editMode: Boolean,
    // Column width at each breakpoint
    // Accepts: 1-12 (grid), percentage string, 'auto', 'grow'
    cols: { type: [String, Number], default: '100%' },
    sm: { type: [String, Number], default: null },
    md: { type: [String, Number], default: null },
    lg: { type: [String, Number], default: null },
    xl: { type: [String, Number], default: null },
    // Self alignment (overrides row's align for this column)
    alignSelf: {
      type: String,
      default: null,
      validator: (v) =>
        v === null ||
        ['start', 'center', 'end', 'stretch', 'baseline'].includes(v),
    },
    // Order at each breakpoint
    order: { type: [String, Number], default: null },
    orderSm: { type: [String, Number], default: null },
    orderMd: { type: [String, Number], default: null },
    orderLg: { type: [String, Number], default: null },
    orderXl: { type: [String, Number], default: null },
    // Custom class
    className: { type: String, default: '' },
  },
  computed: {
    colClasses() {
      const classes = [this.className];

      // Add alignment class if specified
      if (this.alignSelf) {
        classes.push(`page-col--align-${this.alignSelf}`);
      }

      return classes;
    },
    colStyle() {
      const style = {};

      // Set width CSS custom properties for each breakpoint
      style['--col-width-base'] = this.parseWidth(this.cols);

      if (this.sm !== null) {
        style['--col-width-sm'] = this.parseWidth(this.sm);
      }
      if (this.md !== null) {
        style['--col-width-md'] = this.parseWidth(this.md);
      }
      if (this.lg !== null) {
        style['--col-width-lg'] = this.parseWidth(this.lg);
      }
      if (this.xl !== null) {
        style['--col-width-xl'] = this.parseWidth(this.xl);
      }

      // Set order CSS custom properties
      if (this.order !== null) {
        style['--col-order-base'] = this.order;
      }
      if (this.orderSm !== null) {
        style['--col-order-sm'] = this.orderSm;
      }
      if (this.orderMd !== null) {
        style['--col-order-md'] = this.orderMd;
      }
      if (this.orderLg !== null) {
        style['--col-order-lg'] = this.orderLg;
      }
      if (this.orderXl !== null) {
        style['--col-order-xl'] = this.orderXl;
      }

      return style;
    },
  },
  methods: {
    /**
     * Parse width value into CSS-compatible format
     * @param {string|number} value - Width value
     * @returns {string} CSS width value
     */
    parseWidth(value) {
      if (value === 'auto') {
        return 'auto';
      }
      if (value === 'grow') {
        return 'grow';
      }
      // If it's already a percentage string, use it directly
      if (typeof value === 'string' && value.includes('%')) {
        return value;
      }
      // If it's a number 1-12, convert to percentage (12-column grid)
      const num = parseInt(value, 10);
      if (!isNaN(num) && num >= 1 && num <= 12) {
        return `${(num / 12) * 100}%`;
      }
      // Default to 100%
      return '100%';
    },
  },
};
</script>

<style lang="less">
// Breakpoint values (matching Vuetify defaults)
@screen-sm: 600px;
@screen-md: 960px;
@screen-lg: 1280px;
@screen-xl: 1920px;

.page-col {
  // Inherit gap from parent row via CSS custom property
  padding-left: calc(var(--page-row-gap, 24px) / 2);
  padding-right: calc(var(--page-row-gap, 24px) / 2);
  box-sizing: border-box;
  min-width: 0; // Prevent content from forcing column wider

  // Base width (mobile first)
  flex: 0 0 var(--col-width-base, 100%);
  width: var(--col-width-base, 100%);
  max-width: var(--col-width-base, 100%);

  // Handle 'grow' value
  &[style*='--col-width-base: grow'] {
    flex: 1 1 0%;
    width: auto;
    max-width: none;
  }

  // Handle 'auto' value
  &[style*='--col-width-base: auto'] {
    flex: 0 0 auto;
    width: auto;
    max-width: none;
  }

  // Order
  order: var(--col-order-base, 0);

  // Responsive widths
  @media (min-width: @screen-sm) {
    &[style*='--col-width-sm'] {
      flex: 0 0 var(--col-width-sm);
      width: var(--col-width-sm);
      max-width: var(--col-width-sm);
    }
    &[style*='--col-width-sm: grow'] {
      flex: 1 1 0%;
      width: auto;
      max-width: none;
    }
    &[style*='--col-width-sm: auto'] {
      flex: 0 0 auto;
      width: auto;
      max-width: none;
    }
    &[style*='--col-order-sm'] {
      order: var(--col-order-sm);
    }
  }

  @media (min-width: @screen-md) {
    &[style*='--col-width-md'] {
      flex: 0 0 var(--col-width-md);
      width: var(--col-width-md);
      max-width: var(--col-width-md);
    }
    &[style*='--col-width-md: grow'] {
      flex: 1 1 0%;
      width: auto;
      max-width: none;
    }
    &[style*='--col-width-md: auto'] {
      flex: 0 0 auto;
      width: auto;
      max-width: none;
    }
    &[style*='--col-order-md'] {
      order: var(--col-order-md);
    }
  }

  @media (min-width: @screen-lg) {
    &[style*='--col-width-lg'] {
      flex: 0 0 var(--col-width-lg);
      width: var(--col-width-lg);
      max-width: var(--col-width-lg);
    }
    &[style*='--col-width-lg: grow'] {
      flex: 1 1 0%;
      width: auto;
      max-width: none;
    }
    &[style*='--col-width-lg: auto'] {
      flex: 0 0 auto;
      width: auto;
      max-width: none;
    }
    &[style*='--col-order-lg'] {
      order: var(--col-order-lg);
    }
  }

  @media (min-width: @screen-xl) {
    &[style*='--col-width-xl'] {
      flex: 0 0 var(--col-width-xl);
      width: var(--col-width-xl);
      max-width: var(--col-width-xl);
    }
    &[style*='--col-width-xl: grow'] {
      flex: 1 1 0%;
      width: auto;
      max-width: none;
    }
    &[style*='--col-width-xl: auto'] {
      flex: 0 0 auto;
      width: auto;
      max-width: none;
    }
    &[style*='--col-order-xl'] {
      order: var(--col-order-xl);
    }
  }

  // Self-alignment variants
  &--align-start {
    align-self: flex-start;
  }
  &--align-center {
    align-self: center;
  }
  &--align-end {
    align-self: flex-end;
  }
  &--align-stretch {
    align-self: stretch;
  }
  &--align-baseline {
    align-self: baseline;
  }
}

// When inside a no-gutters row
.page-row--no-gutters .page-col {
  padding-left: 0;
  padding-right: 0;
}
</style>
